import { Field } from '@hive/ui';
import type { StreamSetup } from '../api/client';

export interface StreamFormState {
  name: string;
  code: string;
  description: string;
  streamUrl: string;
  backupUrl: string;
  streamType: StreamSetup['streamType'];
  contribution: StreamSetup['contribution'];
  adRights: StreamSetup['adRights'];
  scte35: StreamSetup['scte35'];
}

export const emptyStream: StreamFormState = {
  name: '',
  code: '',
  description: '',
  streamUrl: 'https://',
  backupUrl: '',
  streamType: 'dash',
  contribution: 'channel',
  adRights: 'around',
  scte35: 'unknown',
};

export function streamForm(s: StreamSetup): StreamFormState {
  return {
    name: s.name,
    code: s.code ?? '',
    description: s.description ?? '',
    streamUrl: s.streamUrl,
    backupUrl: s.backupUrl ?? '',
    streamType: s.streamType,
    contribution: s.contribution,
    adRights: s.adRights,
    scte35: s.scte35,
  };
}

/** What a stream carries, and how venues get it. */
export const CONTRIBUTION: Record<StreamSetup['contribution'], { label: string; hint: string }> = {
  channel: { label: 'Channel', hint: 'Always on. Venues with a channel right to it can put it on any screen.' },
  event: { label: 'One-off event', hint: 'A single match or show. Venues get it through the event scheduled on it.' },
  competition: { label: 'Competition feed', hint: "Carries a competition's fixtures. Venues get them through competition or fixture rights." },
  loop: { label: 'Sponsor or ambient loop', hint: 'Loops between fixtures. Every venue gets them; no rights needed.' },
};

export const AD_RIGHTS: Record<StreamSetup['adRights'], { label: string; hint: string }> = {
  replace: { label: 'Replace breaks', hint: "The TV may swap the broadcast's ad breaks at SCTE-35 markers" },
  around: { label: 'Around only', hint: 'Never cover the broadcast; ads before, after and between' },
  overlay: { label: 'Overlay', hint: 'Squeeze-back, L-shape or corner placements over content' },
  none: { label: 'No ads', hint: 'No ads with this stream' },
};

export const SCTE35: Record<StreamSetup['scte35'], string> = {
  unknown: 'Not known yet',
  none: 'No markers',
  dash_event_stream: 'DASH EventStream (in the manifest)',
  dash_emsg: 'DASH emsg (in the segments)',
  hls_daterange: 'HLS EXT-X-DATERANGE',
  hls_cue: 'HLS EXT-X-CUE-OUT',
};

/** The stream's fields, shared by "Add a stream" and the stream page. */
export function StreamFields({ form, onChange }: { form: StreamFormState; onChange(next: StreamFormState): void }) {
  const set = <K extends keyof StreamFormState>(key: K) => (value: StreamFormState[K]) => onChange({ ...form, [key]: value });
  return (
    <div className="mc-form">
      <div className="hv-field">
        <label className="hv-field__label" htmlFor="st-carries">
          What it carries
        </label>
        <select id="st-carries" className="hv-input" value={form.contribution} onChange={(e) => set('contribution')(e.target.value as StreamFormState['contribution'])}>
          {Object.entries(CONTRIBUTION).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <span className="mc-tiny hv-muted">{CONTRIBUTION[form.contribution].hint}</span>
      </div>
      <div className="mc-grid2">
        <Field id="st-name" label="Name" placeholder="Gulf Premier League 1" value={form.name} onChange={(e) => set('name')(e.target.value)} required maxLength={80} />
        <Field id="st-code" label="Feed code (optional)" placeholder="GPL-01" value={form.code} onChange={(e) => set('code')(e.target.value)} maxLength={40} />
      </div>
      <div className="mc-grid-url">
        <Field id="st-url" label="Stream address" value={form.streamUrl} onChange={(e) => set('streamUrl')(e.target.value)} required maxLength={2000} />
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="st-type">
            Format
          </label>
          <select id="st-type" className="hv-input" value={form.streamType} onChange={(e) => set('streamType')(e.target.value as StreamFormState['streamType'])}>
            <option value="dash">DASH</option>
            <option value="hls">HLS</option>
          </select>
        </div>
      </div>
      <Field
        id="st-backup"
        label="Backup address (optional): players switch to it after repeated errors"
        placeholder="https://backup-cdn.example.com/…"
        value={form.backupUrl}
        onChange={(e) => set('backupUrl')(e.target.value)}
        maxLength={2000}
      />
      <div className="mc-grid2">
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="st-ads">
            Ad rights
          </label>
          <select id="st-ads" className="hv-input" value={form.adRights} onChange={(e) => set('adRights')(e.target.value as StreamFormState['adRights'])}>
            {Object.entries(AD_RIGHTS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <span className="mc-tiny hv-muted">{AD_RIGHTS[form.adRights].hint}</span>
        </div>
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="st-scte">
            Ad-break markers
          </label>
          <select id="st-scte" className="hv-input" value={form.scte35} onChange={(e) => set('scte35')(e.target.value as StreamFormState['scte35'])}>
            {Object.entries(SCTE35).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Field id="st-desc" label="Description (optional)" value={form.description} onChange={(e) => set('description')(e.target.value)} maxLength={300} />
    </div>
  );
}
